import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * <AnimatedSprite2D> draws a SpriteFrames animation as a textured quad, like a
 * Sprite2D. It is a selection-driven transport driver (ADR-0012), not an autonomous
 * loop: it shows the authored `frame` until the node is selected and the transport
 * plays. The pure `stepPlayback` reducer decides each frame's transport action.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { CanvasItem2D } from '../../../r3f/components/CanvasItem2D';
import { frameSizePx, WHOLE_FRAME } from '../../../r3f/spriteFrame';
import { useNode2DTextureSampler } from '../../../r3f/canvasItemTextureSampler';
import { canvasMapProps, useCanvas2DTexture } from '../../../r3f/canvas2DTextureDecode';
import { materialProgramInputs } from '../../../r3f/materialProgramInputs';
import { useTexture2D } from '../../../resources/useTexture2D';
import { MissingResourcePlaceholder } from '../../../r3f/components/MissingResourcePlaceholder';
import { useAnimationTransport } from '../../../r3f/contexts/AnimationTransportContext';
import { stepPlayback } from '../../../r3f/animation/stepPlayback';
import { loopsUnderOverride } from '../../../r3f/animation/loopOverride';
import { useIsSelected } from '../../../r3f/hooks/useIsSelected';
import { useSpriteFrames } from '../../../resources/textures/spriteframes/useSpriteFrames';
import { frameAtTime, clipDuration } from '../../../resources/textures/spriteframes/playback';
import { isLocalToScene } from '../../../resources/textures/viewporttexture/localToScene.js';
const NO_EXTERNAL_RESOURCES = [];
const NO_INTERNAL_RESOURCES = [];
export function AnimatedSprite2D({ node, children }) {
    const props = node.properties;
    // Every animation the SpriteFrames declares, not only the authored one: the
    // transport lets the user pick any clip.
    const { spriteFrames: resolved, status: spriteFramesStatus } = useSpriteFrames(props.sprite_frames);
    const spriteFrames = resolved?.animations ?? null;
    const clipNames = useMemo(() => (spriteFrames ? [...spriteFrames.keys()] : []), [spriteFrames]);
    const durations = useMemo(() => spriteFrames
        ? Object.fromEntries([...spriteFrames.values()].map((a) => [a.name, clipDuration(a)]))
        : {}, [spriteFrames]);
    // This sprite owns the transport only while it is the node selected in the tree.
    const transport = useAnimationTransport();
    const isActive = useIsSelected();
    // Register this sprite's clips with the transport while selected, so the
    // Animation tab lists them (the authored `animation` pre-selects).
    const { registerPlayer } = transport;
    useEffect(() => {
        if (!isActive || !spriteFrames)
            return;
        return registerPlayer({ clips: clipNames, durations, autoplay: props.animation || undefined });
    }, [isActive, spriteFrames, clipNames, durations, props.animation, registerPlayer]);
    // The clip to display: the transport's selected clip while active, else the
    // authored `animation` (so an unselected/static sprite shows its own clip).
    const effectiveState = isActive ? transport.playState : 'stopped';
    const currentAnim = pickAnimation(spriteFrames, isActive ? transport.selectedClip : props.animation);
    // Playback advances `playbackFrame` from the transport playhead while
    // playing/paused. When stopped the authored `props.frame` is shown directly.
    const [playbackFrame, setPlaybackFrame] = useState(0);
    const { time: transportTime, reportTime } = transport;
    // AnimatedSprite2D has no THREE action to read the live playhead from, so it
    // keeps its own clock. The reducer's `resume` flag re-seeds it from the transport
    // on a fresh play entry and on a clip switch.
    const prevStateRef = useRef(effectiveState);
    const prevAnimRef = useRef(null);
    const prevTimeRef = useRef(0);
    const lastPlayingTimeRef = useRef(0);
    useFrame((_, delta) => {
        if (!currentAnim)
            return;
        const step = stepPlayback({
            prevState: prevStateRef.current,
            state: effectiveState,
            prevTime: prevTimeRef.current,
            transportTime,
            liveTime: lastPlayingTimeRef.current,
            // Treat an animation change as a "clip changed" so resume fires.
            clipChanged: currentAnim.name !== prevAnimRef.current,
        });
        // Pause-edge flush. `liveTime` is always a number here, though
        // `stepPlayback` also accepts null.
        if (step.flushTime) {
            reportTime(lastPlayingTimeRef.current, { immediate: true });
        }
        switch (step.command) {
            case 'ensure-playing': {
                const dur = clipDuration(currentAnim);
                // Re-seed the local clock on a fresh entry into playing or a clip switch.
                const base = step.resume ? transportTime : lastPlayingTimeRef.current;
                // The preview Speed multiplier and Loop override apply as in the mixer
                // drivers (loopsUnderOverride mirrors applyLoopOverride). A repeating clip
                // wraps past its end, and a non-repeating one holds its last frame.
                const s = delta * transport.playbackSpeed;
                const loops = loopsUnderOverride(transport.loopOverride, currentAnim.loop);
                const t = dur > 0 && loops ? (base + s) % dur : Math.min(base + s, dur);
                reportTime(t);
                lastPlayingTimeRef.current = t;
                // frameAtTime reads the animation's own loop flag, so it gets the
                // override-effective flag, not the authored one.
                const effectiveAnim = loops === currentAnim.loop ? currentAnim : { ...currentAnim, loop: loops };
                const next = frameAtTime(effectiveAnim, t);
                setPlaybackFrame((prev) => (prev === next ? prev : next));
                break;
            }
            case 'seek':
            case 'hold-paused': {
                // Paused: with no THREE action to hold, "hold" and "seek" are the same
                // frame lookup at the transport time.
                const next = frameAtTime(currentAnim, transportTime);
                setPlaybackFrame((prev) => (prev === next ? prev : next));
                break;
            }
            case 'stop-and-restore':
            case 'none': {
                // Stopped: the authored props.frame shows below, so nothing drives here.
                break;
            }
        }
        prevStateRef.current = effectiveState;
        prevAnimRef.current = currentAnim.name;
        prevTimeRef.current = transportTime;
    });
    const frameCount = currentAnim?.frames.length ?? 0;
    const rawFrame = effectiveState === 'stopped' ? props.frame : playbackFrame;
    // Godot clamps an out-of-range frame to the last rather than blanking.
    const frame = frameCount > 0 ? Math.min(Math.max(rawFrame, 0), frameCount - 1) : 0;
    const frameRef = currentAnim?.frames[frame] ?? null;
    // A frame's ids are scoped to its SpriteFrames' home, so the frame resolves
    // against those pools: the scene's, or the .tres's.
    const { texture: frameTexture, size: frameSize, missing: frameMissing, } = useTexture2D(frameRef ?? undefined, resolved?.externalResources ?? NO_EXTERNAL_RESOURCES, resolved?.subResources ?? NO_INTERNAL_RESOURCES, 'plain', resolved ? isLocalToScene(resolved.resource) : undefined);
    // `useCanvas2DTexture` clones each frame texture with the item's sampler and disposes the clone
    // once it no longer draws, so playback leaks no GPU texture.
    const sampler = useNode2DTextureSampler(props);
    const displayedTexture = useCanvas2DTexture(frameTexture, sampler);
    const { width, height } = useMemo(() => frameSizePx(frameSize, WHOLE_FRAME), [frameSize]);
    // No SpriteFrames, no animation or no frame texture draws nothing (`animated_sprite_2d.cpp:270-278`),
    // and so does a `.tres` still loading. The placeholder is for a resource that is set but failed.
    const spriteFramesFailed = !!props.sprite_frames && spriteFramesStatus === 'unavailable';
    const showPlaceholder = spriteFramesFailed || (!!frameRef && frameMissing);
    const cgx = props.offset.x + (props.centered ? 0 : width / 2);
    const cgy = props.offset.y + (props.centered ? 0 : height / 2);
    const meshScale = [props.flip_h ? -1 : 1, props.flip_v ? -1 : 1, 1];
    return (_jsx(CanvasItem2D, { node: node, props: props, body: ({ color, opacity }, _material, pixelMaterial) => {
            if (showPlaceholder)
                return _jsx(MissingResourcePlaceholder, { shape: "plane", name: node.name });
            if (!displayedTexture)
                return null;
            // Constant while a clip plays, since each frame's clone decodes alike, so
            // this remounts when the map appears, not per frame.
            const program = materialProgramInputs({
                props: {
                    ...canvasMapProps(displayedTexture),
                    color,
                    opacity,
                    transparent: true,
                    depthWrite: false,
                },
                merge: [pixelMaterial],
            });
            return (_jsxs("mesh", { position: [cgx, -cgy, 0], scale: meshScale, children: [_jsx("planeGeometry", { args: [width, height] }), _jsx("meshBasicMaterial", { ...program.props }, program.key)] }));
        }, children: children }));
}
/** Pick the named animation, falling back to the first declared one. */
function pickAnimation(map, name) {
    if (!map)
        return null;
    if (name && map.has(name))
        return map.get(name);
    return [...map.values()][0] ?? null;
}
//# sourceMappingURL=Component.js.map